import React from "react";
import axios from "axios";
import Head from "next/head";
import { Button } from "ui";
import LogoIcon from "../../components/Icons/LogoIcon";
import { ForgotPasswordIllustration } from "../../components/Illustrations/ForgotPasswordIllustration";
import Logo from "../../components/Icons/Logo";
import { useRouter } from "next/router";
import {
  Form,
  FormField,
  FormLabel,
  FormErrorMessage,
  InputFormField,
  useFormState,
} from "../../features/form";
import { FieldValues } from "react-hook-form";
import { parseErrorsToObject } from "../../utils/parseErrors";
import { CheckCircleIcon } from "icons/outline";
import { analytics } from "../../features/analytics/analyticsManager";

export default function ForgotPassword() {
  const [isSubmitted, setIsSubmitted] = React.useState<boolean>(false);
  const handleSubmit = async ({ email }: FieldValues) => {
    return axios
      .post(`${process.env.NEXT_PUBLIC_API_BASE}/users/reset-password/`, {
        email,
      })
      .then(() => {
        analytics.track("user_forgot_password", { email });
        setIsSubmitted(true);
      })
      .catch((err) => {
        const errors = parseErrorsToObject(err);
        return {
          errors: {
            ...errors,
            root: errors["non_field_errors"] || "Something went wrong.",
          },
        };
      });
  };

  return (
    <div className="flex min-h-screen">
      <Head>
        <title>{process.env.NEXT_PUBLIC_APP_TITLE} | Reset your password</title>
        <meta name="description" content="Reset your password" />
        <link rel="icon" href="/favicon.ico" />
      </Head>
      <div className="hidden w-[47%] flex-col bg-[rgba(var(--neutral-light-800))] p-8 lg:flex">
        <LogoIcon className="text-white" />

        <div className="h-full ">
          <div className="flex h-full items-center justify-center">
            <ForgotPasswordIllustration />
          </div>
        </div>
      </div>
      <div className="flex flex-1 flex-col justify-center bg-foundations-subtlest px-4 py-12 sm:px-6 lg:px-20 xl:px-24">
        <div className="mx-auto w-full max-w-[580px] rounded-lg border border-foundations-subtle bg-foundations-default p-8">
          {isSubmitted ? (
            <>
              <Logo />
              <div className="my-6 flex flex-col items-center justify-center">
                <CheckCircleIcon className="h-10 w-10 text-success" />
                <p className="text-lg font-bold text-foundations-boldest">
                  We sent you a reset link.
                </p>
                <p className="text-sm text-foundations-boldest">
                  Check your email to reset your password and login with your
                  new password.
                </p>
              </div>
            </>
          ) : (
            <>
              <div>
                <Logo />
                <h1 className="mt-6 text-2xl font-medium text-foundations-boldest">
                  Forgot Password?
                </h1>
                <p className="mt-1 text-left text-sm text-foundations-subtle">
                  It&apos;s okay, we got you covered.
                  <br />
                  Just enter the email address and we&apos;ll send you a reset
                  link
                </p>
              </div>

              <div className="mt-8">
                <div className="mt-6">
                  <Form className="space-y-6" onSubmit={handleSubmit}>
                    <FormField
                      label="Email"
                      name="email"
                      required
                      validation="required|email"
                    >
                      <FormLabel />
                      <InputFormField
                        placeholder="Enter your email"
                        type="email"
                      />
                      <FormErrorMessage />
                    </FormField>

                    <FormActions />

                    <FormServerErrors />
                  </Form>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const FormActions = () => {
  const { isSubmitting } = useFormState();
  const router = useRouter();

  return (
    <>
      <Button
        variant="primary"
        type="submit"
        className="mb-2 w-full"
        isLoading={isSubmitting}
      >
        Send
      </Button>
      <Button
        variant="tertiary"
        type="button"
        className="w-full"
        onClick={() => router.push("/")}
      >
        Go back
      </Button>
    </>
  );
};

const FormServerErrors = () => {
  const { errors } = useFormState();

  if (!errors.root?.message) return null;

  return (
    <p className="mt-2 text-center text-sm text-danger">
      {errors.root?.message}
    </p>
  );
};

ForgotPassword.publicPath = true;
